import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { initUserManager, storeSessionOnServer } from "./AuthProvider";
import useSessionTimer from "../session/hooks/useSessionTimer";
import { logger } from "../../shared/utils/logger";
import { useTranslation } from "react-i18next";

const overlayStyles: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};
const modalStyles: React.CSSProperties = {
  background: "#fff",
  padding: "24px 28px",
  borderRadius: 12,
  minWidth: 360,
  maxWidth: 640,
  textAlign: "left",
  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
};
const btnStyles: React.CSSProperties = {
  marginTop: 16,
  display: "inline-block",
  padding: "10px 14px",
  borderRadius: 8,
  background: "#1976d2",
  color: "#fff",
  border: 0,
  cursor: "pointer",
  fontWeight: 600,
};
const preStyles: React.CSSProperties = {
  background: "#f7f7f9",
  padding: "10px 12px",
  borderRadius: 8,
  overflowX: "auto",
  fontSize: 12,
  lineHeight: 1.4,
};

function readOidcErrorFromUrl(): { error?: string; error_description?: string } {
  const url = new URL(window.location.href);

  const errQ = url.searchParams.get("error") || undefined;
  const descQ = url.searchParams.get("error_description") || undefined;

  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  const hp = new URLSearchParams(hash);
  const errH = hp.get("error") || undefined;
  const descH = hp.get("error_description") || undefined;

  return {
    error: errQ ?? errH,
    error_description: descQ ?? descH,
  };
}

function parseB2CErrorDetails(desc?: string) {
  if (!desc) return {};
  const corrMatch = desc.match(/Correlation ID:\s*([a-f0-9-]{8,})/i);
  const timeMatch = desc.match(/Timestamp:\s*([0-9:\-TZ\s.]+)/i);
  return {
    correlationId: corrMatch?.[1],
    timestamp: timeMatch?.[1],
  };
}

function ErrorPanel({
                      title,
                      message,
                      error,
                      description,
                      correlationId,
                      timestamp,
                      onBack,
                    }: {
  title: string;
  message: string;
  error?: string;
  description?: string;
  correlationId?: string;
  timestamp?: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  return (
      <div style={overlayStyles} role="dialog" aria-modal="true" aria-live="assertive">
        <div style={modalStyles}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h2>
          <p style={{ marginTop: 8, fontSize: 14 }}>
            {message}
          </p>

          {error && (
              <div style={{ marginTop: 10 }}>
                <strong>{t("callback.error.label.error")}</strong>
                <pre style={preStyles}>{error}</pre>
              </div>
          )}

          {description && (
              <div style={{ marginTop: 10 }}>
                <strong>{t("callback.error.label.description")}</strong>
                <pre style={preStyles}>{description}</pre>
              </div>
          )}

          {(correlationId || timestamp) && (
              <div style={{ marginTop: 10 }}>
                <strong>{t("callback.error.label.diagnostics")}</strong>
                <pre style={preStyles}>
              {correlationId
                ? `${t("callback.error.diagnostics.correlationId", { value: correlationId })}\n`
                : ""}
              {timestamp
                ? `${t("callback.error.diagnostics.timestamp", { value: timestamp })}\n`
                : ""}
            </pre>
              </div>
          )}

          <button style={btnStyles} onClick={onBack}>
            {t("callback.error.backToLogin")}
          </button>
        </div>
      </div>
  );
}

export default function Callback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const processedRef = useRef(false);
  const { startTimer } = useSessionTimer();
  const { t } = useTranslation();

  const [oidcError, setOidcError] = useState<{
    error?: string;
    description?: string;
    correlationId?: string;
    timestamp?: string;
  } | null>(null);

  const clearRedirectFlags = () => {
    try { localStorage.removeItem("suppressLogoutModal"); } catch {}
    try {
      sessionStorage.removeItem("oidcRedirect");
      sessionStorage.removeItem("oidcRedirectAt");
    } catch {}
  };

  useEffect(() => {
    (async () => {
      if (processedRef.current) return;
      processedRef.current = true;

      try {
        sessionStorage.removeItem("oidcRedirect");
      } catch {}

      const { error: errParam, error_description: errDescParam } = readOidcErrorFromUrl();
      if (errParam || errDescParam) {
        const details = parseB2CErrorDetails(errDescParam);
        logger.error("OIDC error on callback (from URL):", errParam, errDescParam);
        setOidcError({
          error: errParam,
          description: errDescParam,
          correlationId: details.correlationId,
          timestamp: details.timestamp,
        });
        return;
      }

      logger.info("Processing OAuth callback…");
      logger.info({
        origin: window.location.origin,
        href: window.location.href,
        local_loggedMethod: localStorage.getItem("loggedMethod"),
      });

      try {
        const um = await initUserManager();
        const s = um.settings as any;
        logger.info({
          provider: "MYGOV",
          authority: s.authority,
          client_id: s.client_id,
          redirect_uri: s.redirect_uri,
          scope: s.scope,
        });

        const user = await um.signinRedirectCallback();
        if (!user?.id_token) throw new Error("No id_token in callback result");

        await storeSessionOnServer(user.access_token ?? "", { idToken: user.id_token });

        await startTimer();

        clearRedirectFlags();

        navigate(`/preview`);
      } catch (err: any) {
        const msg = String(err?.message || err);
        logger.error("Error processing OAuth callback:", err);

        const details = parseB2CErrorDetails(err?.message || err?.error_description);
        setOidcError({
          error: err?.error || "callback_error",
          description: err?.error_description || msg,
          correlationId: details.correlationId,
          timestamp: details.timestamp,
        });
      }
    })();
  }, [navigate, params, startTimer]);

  if (oidcError) {
    return (
        <>
          <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
          <ErrorPanel
              title={t("callback.error.title")}
              message={t("callback.error.message")}
              error={oidcError.error}
              description={oidcError.description}
              correlationId={oidcError.correlationId}
              timestamp={oidcError.timestamp}
              onBack={() => {
                try { localStorage.removeItem("suppressLogoutModal"); } catch {}
                try {
                  sessionStorage.removeItem("oidcRedirect");
                  sessionStorage.removeItem("oidcRedirectAt");
                } catch {}
                navigate("/login", { replace: true });
              }}
          />
        </>
    );
  }

  return (
      <>
        <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
        <div
            role="status"
            aria-live="assertive"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
        >
          <div
              style={{
                background: "#fff",
                padding: "24px 28px",
                borderRadius: 12,
                minWidth: 320,
                textAlign: "center",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              }}
          >
            <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "4px solid #eee",
                  borderTop: "4px solid #1976d2",
                  animation: "spin 0.9s linear infinite",
                  margin: "0 auto",
                }}
                aria-hidden
            />
            <h2 style={{ marginTop: 12, fontSize: 18, fontWeight: 700 }}>{t("callback.loading.title")}</h2>
            <p style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
              {t("callback.loading.subtitle")}
            </p>
          </div>
        </div>
      </>
  );
}
